/**
 * Sentry utility functions for privacy and security
 */

/**
 * Sensitive query parameters that should be redacted from URLs
 */
const SENSITIVE_PARAMS = [
  'token',
  'auth',
  'password',
  'secret',
  'api_key',
  'apiKey',
  'access_token',
  'refresh_token',
  'session',
  'code',
  'state'
]

/**
 * Redacts sensitive query parameters from a URL
 * @param url - The URL to redact
 * @returns The URL with sensitive parameters replaced with [REDACTED]
 */
export function redactSensitiveParams(url: string): string {
  try {
    const urlObj = new URL(url)
    const params = urlObj.searchParams

    for (const param of SENSITIVE_PARAMS) {
      if (params.has(param)) {
        params.set(param, '[REDACTED]')
      }
    }

    return urlObj.toString()
  } catch {
    // If URL parsing fails, return original
    return url
  }
}

/**
 * Checks if a string appears to contain PII
 * @param value - The value to check
 * @returns True if the value likely contains PII
 */
export function isPII(value: string): boolean {
  // Email pattern
  if (/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/.test(value)) {
    return true
  }

  // Phone number pattern
  if (/\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/.test(value)) {
    return true
  }

  // Credit card pattern
  if (/\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/.test(value)) {
    return true
  }

  return false
}

/**
 * Redacts PII from error messages and contexts
 * @param data - The data to sanitize
 * @returns Sanitized data with PII removed
 */
export function sanitizePII(data: Record<string, unknown>): Record<string, unknown> {
  const sanitized: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(data)) {
    if (typeof value === 'string') {
      sanitized[key] = isPII(value) ? '[REDACTED]' : value
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizePII(value as Record<string, unknown>)
    } else {
      sanitized[key] = value
    }
  }

  return sanitized
}
